/**
 * Cross-tier games registry. Both server (ws-router) and client (web)
 * load the same manifest list from this module so the catalog is
 * single-sourced. Server-only code (importing implementation modules)
 * lives in ./server. Client-only code (lazy-loaded modules + tile data)
 * lives in ./client.
 *
 * Add a new game in three steps:
 *   1. Drop a folder under games/<id>/ with manifest.json + client/ + server/.
 *   2. Append the manifest import + entry below.
 *   3. ws-router and the web tile grid pick it up automatically.
 */
import type { GameManifest } from '@tt/types';
import tapRaceManifest from '../../../games/tap-race/manifest.json' with { type: 'json' };
import witchSwitchManifest from '../../../games/witch-switch/manifest.json' with { type: 'json' };
import tarotTapManifest from '../../../games/tarot-tap/manifest.json' with { type: 'json' };
import spiceRushManifest from '../../../games/spice-rush/manifest.json' with { type: 'json' };
import traceRaceManifest from '../../../games/trace-race/manifest.json' with { type: 'json' };
import ouijaManifest from '../../../games/ouija-roulette/manifest.json' with { type: 'json' };
import quickDrawManifest from '../../../games/quick-draw/manifest.json' with { type: 'json' };
import oneOfUsManifest from '../../../games/one-of-us/manifest.json' with { type: 'json' };
import coinConveyerManifest from '../../../games/coin-conveyer/manifest.json' with { type: 'json' };
import hexagonHeatManifest from '../../../games/hexagon-heat/manifest.json' with { type: 'json' };
import lastLanternManifest from '../../../games/last-lantern/manifest.json' with { type: 'json' };
import lumberTumbleManifest from '../../../games/lumber-tumble/manifest.json' with { type: 'json' };
import climbSpireManifest from '../../../games/climb-spire/manifest.json' with { type: 'json' };
import burnstileManifest from '../../../games/burnstile/manifest.json' with { type: 'json' };
import stickBrawlManifest from '../../../games/stick-brawl/manifest.json' with { type: 'json' };
import draftDuelManifest from '../../../games/draft-duel/manifest.json' with { type: 'json' };
import cipherCellarManifest from '../../../games/cipher-cellar/manifest.json' with { type: 'json' };
import quillRaceManifest from '../../../games/quill-race/manifest.json' with { type: 'json' };
import skySieveManifest from '../../../games/sky-sieve/manifest.json' with { type: 'json' };
import traplineManifest from '../../../games/trapline/manifest.json' with { type: 'json' };
import splatterSquareManifest from '../../../games/splatter-square/manifest.json' with { type: 'json' };
import plinkoTavernManifest from '../../../games/plinko-tavern/manifest.json' with { type: 'json' };
import piratePulleyManifest from '../../../games/pirate-pulley/manifest.json' with { type: 'json' };
import ropeWalkManifest from '../../../games/rope-walk/manifest.json' with { type: 'json' };
import cellarSweepManifest from '../../../games/cellar-sweep/manifest.json' with { type: 'json' };
import phantomPursuitManifest from '../../../games/phantom-pursuit/manifest.json' with { type: 'json' };
import heistHeistManifest from '../../../games/heist-heist/manifest.json' with { type: 'json' };
import bannerBashManifest from '../../../games/banner-bash/manifest.json' with { type: 'json' };
import stackSmackManifest from '../../../games/stack-smack/manifest.json' with { type: 'json' };
import relayForgeManifest from '../../../games/relay-forge/manifest.json' with { type: 'json' };
import hexArenaManifest from '../../../games/hex-arena/manifest.json' with { type: 'json' };

export type PlayerFormatId = 'ffa' | 'duel' | 'team-2v2' | 'one-v-three' | 'coop';

export interface PlayerFormat {
  id: PlayerFormatId;
  label: string;
  minPlayers: number;
  maxPlayers: number;
  /** Exact means playerCount must equal min/max. Range means any count in the interval works. */
  countMode: 'exact' | 'range';
}

export interface GameSelection {
  id: string;
  baseId: string;
  formatId: PlayerFormatId | null;
}

export const ALL_MANIFESTS: GameManifest[] = [
  tapRaceManifest as unknown as GameManifest,
  witchSwitchManifest as unknown as GameManifest,
  tarotTapManifest as unknown as GameManifest,
  spiceRushManifest as unknown as GameManifest,
  traceRaceManifest as unknown as GameManifest,
  ouijaManifest as unknown as GameManifest,
  quickDrawManifest as unknown as GameManifest,
  oneOfUsManifest as unknown as GameManifest,
  coinConveyerManifest as unknown as GameManifest,
  hexagonHeatManifest as unknown as GameManifest,
  lastLanternManifest as unknown as GameManifest,
  lumberTumbleManifest as unknown as GameManifest,
  climbSpireManifest as unknown as GameManifest,
  burnstileManifest as unknown as GameManifest,
  stickBrawlManifest as unknown as GameManifest,
  draftDuelManifest as unknown as GameManifest,
  cipherCellarManifest as unknown as GameManifest,
  quillRaceManifest as unknown as GameManifest,
  skySieveManifest as unknown as GameManifest,
  traplineManifest as unknown as GameManifest,
  splatterSquareManifest as unknown as GameManifest,
  plinkoTavernManifest as unknown as GameManifest,
  piratePulleyManifest as unknown as GameManifest,
  ropeWalkManifest as unknown as GameManifest,
  cellarSweepManifest as unknown as GameManifest,
  phantomPursuitManifest as unknown as GameManifest,
  heistHeistManifest as unknown as GameManifest,
  bannerBashManifest as unknown as GameManifest,
  stackSmackManifest as unknown as GameManifest,
  relayForgeManifest as unknown as GameManifest,
  hexArenaManifest as unknown as GameManifest,
];

const FORMAT_OVERRIDES: Record<string, PlayerFormat[]> = {
  'banner-bash': [
    { id: 'ffa', label: 'FFA banner grab', minPlayers: 2, maxPlayers: 8, countMode: 'range' },
    { id: 'team-2v2', label: '2v2 capture the banner', minPlayers: 4, maxPlayers: 4, countMode: 'exact' },
  ],
  'splatter-square': [
    { id: 'ffa', label: 'FFA paint brawl', minPlayers: 2, maxPlayers: 8, countMode: 'range' },
    { id: 'team-2v2', label: '2v2 paint teams', minPlayers: 4, maxPlayers: 4, countMode: 'exact' },
  ],
  'cellar-sweep': [{ id: 'coop', label: '4-player co-op', minPlayers: 4, maxPlayers: 4, countMode: 'exact' }],
  'heist-heist': [{ id: 'team-2v2', label: '2v2 thieves vs guards', minPlayers: 4, maxPlayers: 4, countMode: 'exact' }],
  'phantom-pursuit': [{ id: 'one-v-three', label: '1 phantom vs 3 hunters', minPlayers: 4, maxPlayers: 4, countMode: 'exact' }],
  'pirate-pulley': [
    { id: 'duel', label: '1v1 pulley duel', minPlayers: 2, maxPlayers: 2, countMode: 'exact' },
    { id: 'team-2v2', label: '2v2 pulley crew', minPlayers: 4, maxPlayers: 4, countMode: 'exact' },
  ],
  'relay-forge': [
    { id: 'duel', label: '1v1 forge duel', minPlayers: 2, maxPlayers: 2, countMode: 'exact' },
    { id: 'team-2v2', label: '2v2 relay forge', minPlayers: 4, maxPlayers: 4, countMode: 'exact' },
  ],
  'stack-smack': [
    { id: 'duel', label: '1v1 stack duel', minPlayers: 2, maxPlayers: 2, countMode: 'exact' },
    { id: 'team-2v2', label: '2v2 stack duel', minPlayers: 4, maxPlayers: 4, countMode: 'exact' },
  ],
  'one-of-us': [{ id: 'one-v-three', label: '1 hidden role, 3-8 players', minPlayers: 3, maxPlayers: 8, countMode: 'range' }],
};

const SEPARATE_VARIANT_TILES = new Set(['banner-bash', 'splatter-square', 'pirate-pulley', 'relay-forge', 'stack-smack']);

export function selectionId(baseId: string, formatId: PlayerFormatId | null): string {
  return formatId ? `${baseId}:${formatId}` : baseId;
}

export function parseGameSelection(id: string): GameSelection {
  const [baseId, rawFormat] = id.split(':', 2);
  return { id, baseId: baseId ?? id, formatId: (rawFormat as PlayerFormatId | undefined) ?? null };
}

export function selectionIdsFor(m: Pick<GameManifest, 'id' | 'minPlayers' | 'maxPlayers'>): string[] {
  if (!SEPARATE_VARIANT_TILES.has(m.id)) return [m.id];
  return playerFormatsFor(m).map((f) => selectionId(m.id, f.id));
}

export function expandedSelectionIds(): string[] {
  return listEnabled().flatMap((m) => selectionIdsFor(m));
}

function fallbackFormats(m: GameManifest): PlayerFormat[] {
  const min = Math.max(2, m.minPlayers);
  const max = Math.max(min, m.maxPlayers);
  return [{ id: min === 2 && max === 2 ? 'duel' : 'ffa', label: `${min}-${max} players`, minPlayers: min, maxPlayers: max, countMode: 'range' }];
}

export function playerFormatsFor(m: Pick<GameManifest, 'id' | 'minPlayers' | 'maxPlayers'>): PlayerFormat[] {
  const override = FORMAT_OVERRIDES[m.id];
  if (override) return override;
  return fallbackFormats(m as GameManifest);
}

export function formatForSelection(selection: string, m: Pick<GameManifest, 'id' | 'minPlayers' | 'maxPlayers'>): PlayerFormat | null {
  const parsed = parseGameSelection(selection);
  if (parsed.baseId !== m.id) return null;
  if (!parsed.formatId) return null;
  return playerFormatsFor(m).find((f) => f.id === parsed.formatId) ?? null;
}

/** Structural format slice for count matching — accepts both the
 *  registry's PlayerFormat and the server-shipped web GameTile formats. */
export interface CountFormat {
  label: string;
  minPlayers: number;
  maxPlayers: number;
  countMode: 'exact' | 'range';
}

/** Does one format admit this player count? */
export function formatMatchesCount(f: Pick<CountFormat, 'minPlayers' | 'maxPlayers' | 'countMode'>, playerCount: number): boolean {
  if (f.countMode === 'exact') return playerCount === f.minPlayers && playerCount === f.maxPlayers;
  return playerCount >= f.minPlayers && playerCount <= f.maxPlayers;
}

/**
 * THE availability core: matching + human reason strings over an
 * arbitrary format list. Single implementation shared by the manifest
 * path below AND the web's tile path (web/src/features/lobby/
 * gameAvailability.ts was a hand-copied divergent duplicate with
 * hardcoded `?? 4` fallbacks — the 8P program killed it).
 */
export function availabilityForFormats<F extends CountFormat>(
  formats: readonly F[],
  playerCount: number,
): { playable: boolean; reason: string | null; format: F | null } {
  if (playerCount < 2) return { playable: false, reason: 'Needs 2+ players', format: null };
  const format = formats.find((f) => formatMatchesCount(f, playerCount)) ?? null;
  if (format) return { playable: true, reason: null, format };
  const exact = formats.find((f) => f.countMode === 'exact' && f.minPlayers === f.maxPlayers);
  if (exact) return { playable: false, reason: `Needs ${exact.minPlayers} players - ${exact.label}`, format: null };
  const min = Math.min(...formats.map((f) => f.minPlayers));
  const max = Math.max(...formats.map((f) => f.maxPlayers));
  if (playerCount < min) return { playable: false, reason: `Needs ${min}+ players`, format: null };
  if (playerCount > max) return { playable: false, reason: `Works with up to ${max} players`, format: null };
  return { playable: false, reason: 'No format for this player count', format: null };
}

export function playableFormatFor(m: Pick<GameManifest, 'id' | 'minPlayers' | 'maxPlayers'>, playerCount: number): PlayerFormat | null {
  if (playerCount < 2) return null;
  return playerFormatsFor(m).find((f) => formatMatchesCount(f, playerCount)) ?? null;
}

export function gameAvailability(m: Pick<GameManifest, 'id' | 'minPlayers' | 'maxPlayers'>, playerCount: number): { playable: boolean; reason: string | null; format: PlayerFormat | null } {
  return availabilityForFormats(playerFormatsFor(m), playerCount);
}

export function selectionAvailability(selection: string, playerCount: number): { playable: boolean; reason: string | null; format: PlayerFormat | null; manifest: GameManifest | null; baseId: string } {
  const parsed = parseGameSelection(selection);
  const manifest = getManifest(parsed.baseId);
  if (!manifest) return { playable: false, reason: 'Unknown game', format: null, manifest: null, baseId: parsed.baseId };
  if (parsed.formatId) {
    const format = formatForSelection(selection, manifest);
    if (!format) return { playable: false, reason: 'Unknown format', format: null, manifest, baseId: parsed.baseId };
    const playable = format.countMode === 'exact'
      ? playerCount === format.minPlayers && playerCount === format.maxPlayers
      : playerCount >= format.minPlayers && playerCount <= format.maxPlayers;
    return {
      playable,
      reason: playable ? null : (format.countMode === 'exact' && format.minPlayers === format.maxPlayers
        ? `Needs ${format.minPlayers} players - ${format.label}`
        : `Needs ${format.minPlayers}-${format.maxPlayers} players`),
      format: playable ? format : null,
      manifest,
      baseId: parsed.baseId,
    };
  }
  const availability = gameAvailability(manifest, playerCount);
  return { ...availability, manifest, baseId: parsed.baseId };
}

export function isGamePlayable(m: Pick<GameManifest, 'id' | 'minPlayers' | 'maxPlayers'>, playerCount: number): boolean {
  return gameAvailability(m, playerCount).playable;
}

export function eligibleGames(playerCount: number, allowed: string[] | undefined, exclude: string[] = []): GameManifest[] {
  return listEnabled().filter((m) => {
    if (exclude.includes(m.id)) return false;
    if (allowed && allowed.length > 0 && !allowed.includes(m.id)) return false;
    return isGamePlayable(m, playerCount);
  });
}

export function eligibleSelections(playerCount: number, allowed: string[] | undefined, exclude: string[] = []): string[] {
  return expandedSelectionIds().filter((id) => {
    const sel = parseGameSelection(id);
    if (exclude.includes(id) || exclude.includes(sel.baseId)) return false;
    if (allowed && allowed.length > 0 && !allowed.includes(id) && !allowed.includes(sel.baseId)) return false;
    return selectionAvailability(id, playerCount).playable;
  });
}

export function listEnabled(): GameManifest[] {
  return ALL_MANIFESTS.filter((m) => m.enabled);
}

export function getManifest(id: string): GameManifest | null {
  return ALL_MANIFESTS.find((m) => m.id === id) ?? null;
}

export function pickRandom(allowed: string[] | undefined, exclude: string[] = [], playerCount?: number): GameManifest | null {
  const pool = typeof playerCount === 'number'
    ? eligibleGames(playerCount, allowed, exclude)
    : listEnabled().filter((m) => {
      if (exclude.includes(m.id)) return false;
      if (allowed && allowed.length > 0 && !allowed.includes(m.id)) return false;
      return true;
    });
  if (pool.length === 0) {
    // Fall back ignoring exclude if everything was excluded.
    const fallback = typeof playerCount === 'number'
      ? eligibleGames(playerCount, allowed)
      : listEnabled().filter((m) => !allowed || allowed.length === 0 || allowed.includes(m.id));
    return fallback[Math.floor(Math.random() * fallback.length)] ?? null;
  }
  return pool[Math.floor(Math.random() * pool.length)] ?? null;
}

export function pickRandomSelection(allowed: string[] | undefined, exclude: string[] = [], playerCount: number): string | null {
  const pool = eligibleSelections(playerCount, allowed, exclude);
  const fallback = pool.length > 0 ? pool : eligibleSelections(playerCount, allowed);
  return fallback[Math.floor(Math.random() * fallback.length)] ?? null;
}

export function pickNSelections(count: number, allowed: string[] | undefined, exclude: string[] = [], playerCount: number): string[] {
  const out: string[] = [];
  const remaining = eligibleSelections(playerCount, allowed, exclude);
  while (out.length < count && remaining.length > 0) {
    const idx = Math.floor(Math.random() * remaining.length);
    const [pick] = remaining.splice(idx, 1);
    if (pick) out.push(pick);
  }
  return out;
}

export function pickN(count: number, allowed: string[] | undefined, exclude: string[] = [], playerCount?: number): GameManifest[] {
  const out: GameManifest[] = [];
  const pool = typeof playerCount === 'number'
    ? eligibleGames(playerCount, allowed, exclude)
    : listEnabled().filter((m) => !exclude.includes(m.id) && (!allowed || allowed.length === 0 || allowed.includes(m.id)));
  const remaining = [...pool];
  while (out.length < count && remaining.length > 0) {
    const idx = Math.floor(Math.random() * remaining.length);
    const [pick] = remaining.splice(idx, 1);
    if (pick) out.push(pick);
  }
  return out;
}
